import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '#/modules/commonComponents/tabs';
import { isSalesOrderSnMtrComplete } from '#/modules/crmMgmt/salesOrder/helper/salesOrderSnSelection';
import { formatDisplayQty } from '#/utils/formatNumber';

function formatLineLabel(detailLine, index) {
  const product = detailLine?.product;
  const code = String(product?.productNo ?? product?.code ?? '').trim();
  const name = product?.name ? String(product.name).trim() : '';
  return code || name || `產品 ${index + 1}`;
}

function formatMtrLabel(mtr) {
  const code = String(mtr?.mtrNo ?? '').trim();
  const name = mtr?.mtrName ? String(mtr.mtrName).trim() : '';
  if (code && name) {
    return `${code} ${name}`;
  }
  return code || name || '—';
}

/**
 * SN 選用的第二／三層 tabs：產品（lines）→ 物料（lines[].mtrs[]）。
 * 內容由 `renderMtrContent(mtr, selectedIds)` 決定（唯讀清單或勾選）。
 */
const SalesOrderSnLineMtrTabs = ({
  detailLines,
  snLines,
  selectionMap,
  renderMtrContent,
}) => {
  const lineById = useMemo(() => {
    const map = new Map();
    detailLines.forEach((line, index) => map.set(String(line?.id), { line, index }));
    return map;
  }, [detailLines]);

  if (snLines.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">本單沒有需要選擇序號的物料</p>;
  }

  return (
    <Tabs defaultValue={String(snLines[0].salesOrderLineId)} className="w-full gap-3">
      <TabsList className="flex-wrap justify-start bg-slate-100 p-1">
        {snLines.map((snLine) => {
          const lineId = String(snLine.salesOrderLineId);
          const ref = lineById.get(lineId);
          const done = (snLine.mtrs ?? []).every((mtr) => isSalesOrderSnMtrComplete(
            mtr,
            selectionMap[String(mtr.salesOrderLineMtrId)],
          ));
          return (
            <TabsTrigger key={lineId} value={lineId}>
              {formatLineLabel(ref?.line, ref?.index ?? 0)}
              <span className={cn('ml-1 size-1.5 rounded-full', done ? 'bg-emerald-500' : 'bg-amber-500')} />
            </TabsTrigger>
          );
        })}
      </TabsList>

      {snLines.map((snLine) => {
        const lineId = String(snLine.salesOrderLineId);
        const mtrs = snLine.mtrs ?? [];
        return (
          <TabsContent key={lineId} value={lineId} className="mt-0">
            <Tabs defaultValue={String(mtrs[0]?.salesOrderLineMtrId ?? '')} className="w-full gap-3">
              <TabsList variant="line" className="flex-wrap justify-start border-b border-slate-200">
                {mtrs.map((mtr) => {
                  const lineMtrId = String(mtr.salesOrderLineMtrId);
                  const selectedIds = selectionMap[lineMtrId] ?? [];
                  const complete = isSalesOrderSnMtrComplete(mtr, selectedIds);
                  return (
                    <TabsTrigger key={lineMtrId} value={lineMtrId}>
                      {formatMtrLabel(mtr)}
                      <span className={cn('ml-1 text-xs', complete ? 'text-emerald-700' : 'text-amber-700')}>
                        {selectedIds.length}/{formatDisplayQty(mtr.requiredQty)}
                      </span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {mtrs.map((mtr) => {
                const lineMtrId = String(mtr.salesOrderLineMtrId);
                return (
                  <TabsContent key={lineMtrId} value={lineMtrId} className="mt-0">
                    {renderMtrContent(mtr, selectionMap[lineMtrId] ?? [])}
                  </TabsContent>
                );
              })}
            </Tabs>
          </TabsContent>
        );
      })}
    </Tabs>
  );
};

SalesOrderSnLineMtrTabs.propTypes = {
  detailLines: PropTypes.arrayOf(PropTypes.object).isRequired,
  snLines: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectionMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  renderMtrContent: PropTypes.func.isRequired,
};

export default SalesOrderSnLineMtrTabs;
